import { z } from "zod";

/**
 * Use this if you need to loop through all the options
 */
export const RoofScopeValues = [
  "COMP_SHINGLE",
  "ADDITIONAL_LAYERS",
  "BACKFILL_TILE",
  "COMP_OVERLAY",
  "COMP_SHINGLE",
  "CUSTOM_LABOR",
  "CUSTOM_MATERIALS",
  "CUSTOM",
  "SALES_ADJUSTER",
  "DECKING",
  "DORMER_VENT_REPLACEMENT_WITH_FLUSH",
  "DUMPSTER",
  "EPDM",
  "FASCIA_BOARD_REPLACEMENT",
  "FOAM_PENETRATIONS",
  "LICHEN_MOSS_TREATMENT",
  "NEW_TILE",
  "PERMIT",
  "PITCH_10_12",
  "PITCH_11_12",
  "PITCH_12_12",
  "PITCH_6_12",
  "PITCH_7_12",
  "PITCH_8_12",
  "PITCH_9_12",
  "PURLINS",
  "REMOVE_AND_REINSTALL_SOLAR_PANELS",
  "REMOVE_TILE_INSTALL_COMP_SHINGLE",
  "RIDGE_CAP_REPLACE",
  "ROLLED_COMP",
  "ROOFING_QUOTE",
  "SILICONE_COATING",
  "SISTERING",
  "SKYLIGHT_REMOVAL",
  "SOLAR_THERMAL_REMOVAL",
  "THREE_STORY_HOUSE",
  "TILE_RESET_CLAY",
  "TILE_RESET_CONCRETE",
  "TILE_LIFT_AND_RELAY_WITH_OVERLAY",
  "TPO",
  "TRUCK_ROLL_UNDER_8_SQUARES",
  "TRUCK_ROLL_UNDER_8_PANELS",
  "TURBINE_VENT_REPLACEMENT_WITH_FLUSH",
  "TWO_STORY_HOUSE",
  "VENT_REROUTING",
  "REMOVAL_PANELS",
  "REINSTALL_PANELS",
  "PITCH_PANEL_ADDER_8_12",
  "PITCH_PANEL_ADDER_9_12",
  "PITCH_PANEL_ADDER_10_12",
  "PITCH_PANEL_ADDER_11_12",
  "PITCH_PANEL_ADDER_12_12",
  "LOCALIZED_DAMAGE_REPAIR",
  "DAMAGE_ASSESSMENT",
  "R&R_PERMIT",
  "REDESIGN_FEE",
  "DEDUCTIBLE",
  "NON_RECOVERABLE_DEPRECIATION",
  "PARTNER_TOTAL_WORK",
  "HOMEOWNER_ELECTIVE_WORK",
  "GUTTER_REMOVAL",
  "GUTTER_INSTALL_5_IN",
  "GUTTER_INSTALL_6_IN",
  "GUTTER_GUARDS_5_IN",
  "GUTTER_GUARDS_6_IN",
  "PANEL_DISPOSAL",
  "CRITTER_GUARD",
  "HINGE_PLATE_REINFORCEMENT_OR_COLLAR_TIES",
  "TILE_PAN_FLASHING",
  "DOUBLE_HANDLING",
  "PRESIDENTIAL_SHINGLE",
  "CLASS_4_SHINGLE",
  "CUSTOM_SHINGLE",
  "CHIMNEY_FLASHING",
  "TILES_TO_BE_SOURCED",
  "SKYLIGHT_FLASHING",
  "STEP_FLASHING",
  "SOLAR_BRACKETS_REMOVED",
  "CUT_IN_RIDGE_VENT",
  "ADDITIONAL_LAYER_FELT_TEAROFF",
  "MANSARD_ROOF",
  "MATERIAL_ROOF_LOAD",
  "GROUND_EQUIPMENT",
  "SITE_VISIT",
  "SITE_VISIT_REPAIRS",
  "ADJOINING_STRUCTURE",
  "SIDING",
  "ITEL",
] as const;
/**
 * Use this if you need to zod parse it
 */
const RoofScopeSchema = z.enum(RoofScopeValues);
/**
 * Use this if you need to use the literal value
 */
export const RoofScope = RoofScopeSchema.enum;
/**
 * Use this if you need to use the type
 */
export type RoofScope = (typeof RoofScopeValues)[number];
/**
 * Use this if you need to display a human-readable name
 */
export const RoofScopeDisplayNames: Record<RoofScope, string> = {
  ADDITIONAL_LAYERS: "Additional Layers",
  BACKFILL_TILE: "Backfill Tile",
  COMP_OVERLAY: "Comp Overlay",
  COMP_SHINGLE: "Comp Shingle",
  CUSTOM_LABOR: "Custom Labor",
  CUSTOM_MATERIALS: "Custom Materials",
  CUSTOM: "Custom",
  SALES_ADJUSTER: "Sales Adjuster",
  DECKING: "Decking",
  DORMER_VENT_REPLACEMENT_WITH_FLUSH: "Dormer Vent Replacement with Flush",
  DUMPSTER: "Dumpster",
  EPDM: "EPDM",
  FASCIA_BOARD_REPLACEMENT: "Fascia Board Replacement",
  FOAM_PENETRATIONS: "Foam Penetrations",
  LICHEN_MOSS_TREATMENT: "Lichen / Moss Treatment",
  NEW_TILE: "New Tile",
  PERMIT: "Permit",
  PITCH_10_12: "Pitch 10:12",
  PITCH_11_12: "Pitch 11:12",
  PITCH_12_12: "Pitch 12:12",
  PITCH_6_12: "Pitch 6:12",
  PITCH_7_12: "Pitch 7:12",
  PITCH_8_12: "Pitch 8:12",
  PITCH_9_12: "Pitch 9:12",
  PURLINS: "Purlins",
  REMOVE_AND_REINSTALL_SOLAR_PANELS: "Remove and Reinstall Solar Panels",
  REMOVE_TILE_INSTALL_COMP_SHINGLE: "Remove Tile Install Comp Shingle",
  RIDGE_CAP_REPLACE: "Ridge Cap Replace",
  ROLLED_COMP: "Rolled Roofing - Modified Bitumen",
  ROOFING_QUOTE: "Roofing Quote",
  SILICONE_COATING: "Silicone Coating",
  SISTERING: "Sistering",
  SKYLIGHT_REMOVAL: "Skylight Removal",
  SOLAR_THERMAL_REMOVAL: "Solar Thermal Removal",
  THREE_STORY_HOUSE: "Three Story House",
  TILE_RESET_CLAY: "Tile Reset Clay",
  TILE_RESET_CONCRETE: "Tile Reset Concrete",
  TILE_LIFT_AND_RELAY_WITH_OVERLAY: "Tile Lift and Relay with Overlay",
  TPO: "TPO",
  TRUCK_ROLL_UNDER_8_SQUARES: "Truck Roll under 8 squares",
  TRUCK_ROLL_UNDER_8_PANELS: "Truck Roll under 8 panels",
  TURBINE_VENT_REPLACEMENT_WITH_FLUSH: "Turbine Vent Replacement with Flush",
  TWO_STORY_HOUSE: "Two Story House",
  VENT_REROUTING: "Vent Rerouting",
  REMOVAL_PANELS: "Removal Panels",
  REINSTALL_PANELS: "Reinstall Panels",
  PITCH_PANEL_ADDER_8_12: "8:12 Pitch Panel Adder",
  PITCH_PANEL_ADDER_9_12: "9:12 Pitch Panel Adder",
  PITCH_PANEL_ADDER_10_12: "10:12 Pitch Panel Adder",
  PITCH_PANEL_ADDER_11_12: "11:12 Pitch Panel Adder",
  PITCH_PANEL_ADDER_12_12: "12:12 Pitch Panel Adder",
  LOCALIZED_DAMAGE_REPAIR: "Localized Damage Repair",
  DAMAGE_ASSESSMENT: "Damage Assessment",
  "R&R_PERMIT": "R&R Permit",
  REDESIGN_FEE: "Redesign Fee",
  DEDUCTIBLE: "Deductible",
  NON_RECOVERABLE_DEPRECIATION: "Non-Recoverable Depreciation",
  PARTNER_TOTAL_WORK: "Partner Total Work",
  HOMEOWNER_ELECTIVE_WORK: "Homeowner Elective Work",
  GUTTER_REMOVAL: "Gutter Removal",
  GUTTER_INSTALL_5_IN: "Gutter Install 5 in",
  GUTTER_INSTALL_6_IN: "Gutter Install 6 in",
  GUTTER_GUARDS_5_IN: "Gutter Guards 5 in",
  GUTTER_GUARDS_6_IN: "Gutter Guards 6 in",
  PANEL_DISPOSAL: "Panel Disposal",
  CRITTER_GUARD: "Critter Guard",
  HINGE_PLATE_REINFORCEMENT_OR_COLLAR_TIES: "Hinge Plate Reinforcement or Collar Ties",
  TILE_PAN_FLASHING: "Tile Pan Flashing",
  DOUBLE_HANDLING: "Double Handling",
  PRESIDENTIAL_SHINGLE: "Presidential Shingle",
  CLASS_4_SHINGLE: "Class 4 Shingle",
  CUSTOM_SHINGLE: "Custom Shingle",
  CHIMNEY_FLASHING: "Chimney Flashing",
  TILES_TO_BE_SOURCED: "Tiles to be Sourced",
  SKYLIGHT_FLASHING: "Skylight Flashing",
  STEP_FLASHING: "Step Flashing",
  SOLAR_BRACKETS_REMOVED: "Solar Brackets Removed",
  CUT_IN_RIDGE_VENT: "Cut-In Ridge Vent",
  ADDITIONAL_LAYER_FELT_TEAROFF: "Additional Layer Felt Tearoff",
  MANSARD_ROOF: "Mansard Roof",
  MATERIAL_ROOF_LOAD: "Material Roof Load",
  GROUND_EQUIPMENT: "Ground Equipment",
  SITE_VISIT: "Site Visit",
  SITE_VISIT_REPAIRS: "Site Visit Repairs",
  ADJOINING_STRUCTURE: "Adjoining Structure",
  SIDING: "Siding",
  ITEL: "ITEL",
} as const;
